/**
 * API Endpoint Registry
 *
 * All API endpoint URLs must be defined here — never inline strings in components or hooks.
 *
 * ## Naming convention
 * - Export name: `<domain>Endpoints` or `<domain>Requests` — match the closest existing name.
 * - Keys describe **consumer intent** (what the caller is doing), not the resource noun:
 *     ✅  search, report, download, create, update, delete, subscribe
 *     ❌  posts, users, bookings  (resource nouns — too ambiguous)
 *
 * ## Key patterns
 * - Static URL:           `search: '/api/booking/buyers'`
 * - Dynamic URL:          `getById: (id: string) => \`/api/booking/\${id}\``
 * - Grouped sub-actions:  nest under `actions: { ... }` for secondary operations
 *                         (e.g. downloads, state transitions).
 *
 * ## Adding a new domain
 * 1. Add `export const <domain>Endpoints = Object.freeze({ ... })`.
 * 2. Keep all URLs relative (e.g. `/api/...`) — the dev proxy handles the host.
 * 3. Use intent-based key names (see above).
 */

/**
 * Blog Endpoints
 */
export const blogEndpoints = Object.freeze({
  search: '/api/blog/posts',
  subscribe: '/api/blog/subscriptions',
});

//Bookings reports
export const buyerBookingsReportEndpoints = Object.freeze({
  search: "/api/booking/buyers",
});

export const supplierBookingsReportEndpoints = Object.freeze({
  search: "/api/booking/suppliers",
  report: "/api/booking/suppliers/report",
});

export const supplierCurrentInventoryReportEndpoints = Object.freeze({
  search: '/api/suppliers/current-inventory',
  group: '/api/suppliers/current-inventory/grouped',
  report: '/api/suppliers/current-inventory/report',
})

export const adminBookingsReportEndpoints = Object.freeze({
  search: "/api/booking/admins",
  report: "/api/booking/admins/report",
  actions: {
    cancelBooking: "/api/booking/cancel",
    adjustEndDate: "/api/booking/adjust-end-date",
    toggleOverages: "/api/booking/toggle-charge-overages",
    setToManualPayment: "/api/booking/set-to-manual-payment",
    endBooking: "/api/booking/cancel-subscription",
    downloadBookingHistory: (bookingId: string) => `/api/history/booking/${bookingId}`,
    previewNextInvoice: (bookingId: string) => `/api/booking/${bookingId}/next-invoice/preview`,
    createNextInvoice: (bookingId: string) => `/api/booking/${bookingId}/next-invoice`,
    manualOverageInvoiceEligibility: (bookingId: string) => `/api/booking/${bookingId}/manual-overage-invoice/eligibility`,
    previewManualOverageInvoice: (bookingId: string) => `/api/booking/${bookingId}/manual-overage-invoice/preview`,
    createManualOverageInvoice: (bookingId: string) => `/api/booking/${bookingId}/manual-overage-invoice`,
  }
});

//Invoice Reports
export const adminInvoicesReportEndpoints = Object.freeze({
  search: "/api/admins/report/invoices",
  report: "/api/admins/report/invoices/report",
  summary: "/api/admins/report/invoices/summary",
  group: "/api/admins/report/invoices/group-summary-report",
  actions: {
    downloadInvoice: (transactionId: string) => `/api/invoices/${transactionId}/pdf`,
    downloadInventoryLog: (transactionNumber: string | number) => `/api/overage-daily-report-by-invoice-number/${transactionNumber}`,
    downloadTransactionHistory: (transactionId: string) => `/api/history/booking-transaction/${transactionId}`,
  },
});

export const adminPayoutsReportEndpoints = Object.freeze({
  search: "/api/admins/payouts/report",
  report: "/api/admins/payouts/report/download",
  summary: "/api/admins/payouts/report/summary",
});

export const adminInterchangesReportEndpoints = Object.freeze({
  search: '/api/inventory-activity/admin/report',
  report: '/api/inventory-activity/admin/report/download',
  actions: {
    history: (locationInventoryActivityId: string) => `/api/history/location-inventory-activity/${locationInventoryActivityId}`
  }
})

export const locationsEndpoints = Object.freeze({
  names: '/api/location/names',
});

export const adminLocationsReportEndpoints = Object.freeze({
  report: '/api/admins/locations/report',
  download: '/api/admins/locations/report/download'
});
export const adminSpaceUsageReportEndpoints = Object.freeze({
  search: "/api/admins/space-usage/report",
  report: "/api/admins/space-usage/report/download",
  summary: "/api/admins/space-usage/report/summary",
  actions: {
    downloadInventoryLogById: (transactionNumber: string | number) => `/api/overage-daily-report-by-invoice-number/${transactionNumber}`,
    downloadInventoryLogByDate: (locationId: string, buyerAccountId: string, date: string) => `/api/overage-daily-report-by-date/${locationId}/${buyerAccountId}/${date}`
  }
});

export const buyerInventoryReportEndpoints = Object.freeze({
  search: "/api/inventory/buyers",
  report: "/api/inventory/buyers/report",
});

export const buyerInventoryCorrectionReport = Object.freeze({
  search: "/api/buyers/inventory-correction-report",
  review: "/api/inventory-corrections/buyer-review"
});

export const buyerExistingInventoryCorrections = Object.freeze({
  search: `/api/inventory-corrections/buyer`
});

export const buyerInvoicesReportEndpoints = Object.freeze({
  search: "/api/invoices/buyers",
  report: "/api/invoices/buyers/report",
  summary: "/api/invoices/buyers/summary",
});

export const supplierInvoicesReportEndpoints = Object.freeze({
  report: "/api/invoices/suppliers",
  download: "/api/invoices/suppliers/report",
});

export const supplierAccountsReceivableReportEndpoints = Object.freeze({
  search: "/api/invoices/suppliers/accounts-receivable",
  report: "/api/invoices/suppliers/accounts-receivable/report",
  buyerLocationPdf: "/api/invoices/supplier/accounts-receivable/pdf",
  summary: "/api/invoices/suppliers/accounts-receivable/summary",
});

export const supplierBookingCheckInReportEndpoints = Object.freeze({
  search: "/api/booking/suppliers/checkin/report",
});

export const supplierActivitySummaryReportEndpoints = Object.freeze({
  report: '/api/reporting/supplier-activity-summary-paged',
  aggregate: 'api/reporting/supplier-activity-summary-paged/aggregate',
  download: '/api/reporting/supplier-activity-summary/download'
});

export const supplierPendingInventoryActivityReportEndpoints = Object.freeze({
  search: "/api/pending-inventory-activities",
  count: "/api/pending-inventory-activities/count",
  actions: {
    update: (id: string) => `/api/pending-inventory-activities/${id}`,
    resubmit: (id: string) => `/api/pending-inventory-activities/${id}/resubmit`,
    dismiss: (id: string) => `/api/pending-inventory-activities/${id}/dismiss`,
  }
});

// Interchanges Reports
export const buyerInterchangesReportEndpoints = Object.freeze({
  search: "/api/inventory-activity/buyers",
  report: "/api/inventory-activity/buyers/report",
  summaryReport: "/api/inventory-activity/buyers/summary-report",
});

// Interchanges Reports
export const supplierInterchangesReportEndpoints = Object.freeze({
  search: `/api/inventory-activity/supplier`,
  report: `/api/inventory-activity/supplier/report`,
  editActivity: '/api/inventory-activity',
  deleteActivity: (id: string) => `/api/inventory-activity/${id}`,
  confirmBooking: (orderNumber: string) => `/api/booking/orders/${orderNumber}`,
  move: "/api/inventory-move"
});

// Space Usage Reports
export const buyerSpaceUsageReportEndpoints = Object.freeze({
  search: "/api/space-usage/buyers",
  report: "/api/space-usage/buyers/report",
  summary: "/api/space-usage/buyers/summary",
  group: "/api/space-usage/buyers/group-summary-report",
});

export const supplierSpaceUsageReportEndpoints = Object.freeze({
  search: "/api/space-usage/supplier",
  report: "/api/space-usage/supplier/report",
  group: "/api/space-usage/supplier/group-summary-report",
});

export const accountRegistrationEndpoints = Object.freeze({
  verifyEmail: "/api/account/verification-email/verify",
  resendVerification: "/api/account/resend-verification",
  resetPassword: "/api/account/reset-password",
  changePassword: "/api/account/change-password",
});

export const supplierPayoutReportEndpoints = Object.freeze({
  search: "/api/supplier/payouts/report",
  report: "/api/supplier/payouts/report/download",
  summary: "/api/supplier/payouts/report/summary",
  group: "/api/supplier/payouts/report/group-summary-report",
  breakdown: "/api/supplier/payouts/report/breakdown"
});

export const supplierReadyForPayoutReportEndpoints = Object.freeze({
  search: "/api/suppliers/ready-for-payout",
  report: "/api/suppliers/ready-for-payout/report",
  summary: "/api/suppliers/ready-for-payout/summary",
  group: "/api/suppliers/ready-for-payout/group-summary-report",
  actions: {
    initiatePayouts: "/api/suppliers/create-payouts",}
});

export const accountEditEndpoints = Object.freeze({
  accountUser: {
    get: (accountId: string) => `/api/account-users/${accountId}`,
    create: "/api/account-users",
    update: "/api/account-users",
    remove: "/api/account-users/",
  },
  account: {
    create: "/api/account",
    update: "/api/account",
  },
  admin: {
    update: (accountId: string) => `/api/account/${accountId}/admin`,
    delete: "/api/account/delete-account",
    editType: "/api/account/switch-type",
    changeStatus: "/api/account/set-enabled-account",
  },
  fundingSources: {
    create: '/api/account/funding-sources',
    remove: (accountId: string) => `/api/account/${accountId}/funding-sources`,
  },
  cognitoToken: (accountId: string) => `/api/account/${accountId}/cognito/token`,
  conformToS: (accountId: string) => `/api/account/confirm-tos/${accountId}`,
  updateUserAccessibleLocations: (accountId: string) => `/api/account-users/${accountId}`,
  verificationEmail: "/api/account/verification-email",
  adminAccountReport: {
    getAccountReport: "/api/admins/account/report",
    getAccountReportCSV: "/api/admins/account/report/download",
    getCompanyNames: "/api/admins/account/company-names",
  }
});

export const userRequests = Object.freeze({
  admin: {
    getAllBuyers: '/api/account/buyers',
    getAllAccounts: '/api/account/references',
  },
  supplier: {
    getBuyersForSupplier: '/api/account/supplier/buyers',
  }
});

export const bookingRequests = Object.freeze({
  allBuyerBookings: "/api/booking/all-buyer-bookings",
  completeBooking: "/api/booking/complete-booking",
  updateBookingPaymentMethod: "/api/booking/update-payment-method",
  updateBookingStatus: {
     approve: '/api/booking/approve',
     decline: '/api/booking/decline',
   },
   sendConfirmationEmail: "/api/booking/confirmation-email",
   denyRefund: "/api/booking/deny-refund",
   create: "/api/booking",
   cancel: "/api/booking/cancel",
   cancelSubscription: "/api/booking/cancel-subscription",
   calculateBookingChargeAmount: "/api/booking/calculate-booking-charge-amount",
   getBookingById: (bookingId: string) => `/api/booking/${bookingId}`,
   adjustDates: "/api/booking/adjust-dates",
   adjustEndDate: "/api/booking/adjust-end-date",
   adjustNumberOfSpaces: "/api/booking/adjust-number-of-spaces",
   toggleChargeOverages: "/api/booking/toggle-charge-overages",
   setToManualPayment: "/api/booking/set-to-manual-payment",
   bookingByToken: (token: string) => `/api/booking/get-by-token?token=${token}`
});

export const bookingTransactionRequests = Object.freeze({
  failedBookingTransactions: (bookingId: string) => `/api/booking/${bookingId}/transactions/failed`,
  failedBookingTransactionsRetry: (bookingId: string) => `/api/booking/${bookingId}/transactions/failed/retry`,
  getByCorrelationId: `/api/invoices/transactions`
});

export const citiesRequests = Object.freeze({
  search: "/api/cities",
});

export const freightPlaceRequests = Object.freeze({
  list: "/api/public/freight-places",
});

export const supplierProfileRequests = Object.freeze({
  getPublicSupplierProfile: (shortId: string, slug: string) => `/api/public/supplier/${shortId}/${slug}`,
});

export const locationRequests = Object.freeze({
  location: "/api/location",
  locationDraft: "/api/locations",
  locationGet: (locationId: string) => `/api/locations/${locationId}`,
  locationUpdate: (locationId: string) => `/api/locations/${locationId}`,
  capacitySchedule: (locationId: string) => `/api/locations/${locationId}/capacity-schedule`,
  capacityScheduleImpact: (locationId: string) => `/api/locations/${locationId}/capacity-schedule/impact`,
  capacityScheduleUpload: (locationId: string) => `/api/locations/${locationId}/capacity-schedule/upload`,
  capacityScheduleTemplate: (locationId: string) => `/api/locations/${locationId}/capacity-schedule/template`,
  bulkCapacityScheduleUpload: "/api/locations/bulk-capacity-schedule/upload",
  bulkCapacityScheduleTemplate: "/api/locations/bulk-capacity-schedule/template",
  bulkLocationEditExport: "/api/locations/bulk-edit/export",
  bulkLocationEditValidate: "/api/locations/bulk-edit/validate",
  bulkLocationEditApply: "/api/locations/bulk-edit/apply",
  locationManagers: {
    get: (accountId: string) => `/api/account/${accountId}/location-managers`,
    put: (accountId: string) => `/api/account/${accountId}/user`,
  },
  locationById: (locationId: string) => `/api/location/suppliers/${locationId}`,
  locationCapacityById: (locationId: string) => `/api/location/suppliers/${locationId}/hasRequestedCapacity`,
  locationAvailabilityById: (locationId: string) => `/api/location/suppliers/${locationId}/availability`,
  latLongSearch: "/api/location/search",
  latLongSearchLegacy: "/api/location/suppliers",
  featuredLocations: "/api/location/featured-locations",
  locationByIpAddress: "/api/getByIpLocation",
  locationsByBuyer: "/api/location/buyer",
  locationNames: "/api/location/locationName",
  favorite: {
    get: (locationId: string) => `/api/location/${locationId}/favorite`,
    create: (locationId: string) => `/api/location/${locationId}/favorite`,
    remove: (locationId: string) => `/api/location/${locationId}/favorite`,
  },
  getFavorites: "/api/location/favorite",
  searchFavorites: {
    search: "/api/location/favorites",
    cities: "/api/location/favorites/cities",
    states: "/api/location/favorites/states",
  },
  getPreviouslyBooked: "/api/location/booked",
  booked: "/api/location/booked",
  submitForReview: (locationId: string) => `/api/locations/${locationId}/review`,
  clone: (locationId: string) => `/api/locations/${locationId}/clone`,
  activate: (locationId: string) => `/api/locations/${locationId}/activate`,
  deactivate: (locationId: string) => `/api/locations/${locationId}/deactivate`,
  locationDelete: (locationId: string) => `/api/locations/${locationId}`,
  enrichment: "/api/location-enrichment",
  searchSupplierLocations: "/api/locations"
});

export const paymentEndpoints = Object.freeze({
  getFailedPaymentsForBooking: (bookingId: string) => `/api/booking/${bookingId}/transactions/failed`,
  getBookingById: (bookingId: string) => `/api/booking/${bookingId}`,
  bookingTransactionFeeSummary: "/api/booking-transaction/fee-summary",
});

export const inventoryCorrectionEndpoints = Object.freeze({
  supplierSearch: (accountId: string) => `/api/inventory-corrections/supplier/${accountId}`,
  update: (inventoryId: string) => `/api/inventory-corrections/${inventoryId}`,
  submit: "/api/inventory-corrections",
  delete: (correctionId: string) => `/api/inventory-corrections/${correctionId}`,
  getBookingByOrderNumber: (orderNumber: string) => `/api/booking/orders/${orderNumber}`,
  moveAsset: "/api/inventory-move"
});

export const observableEndpoints = Object.freeze({
  buyer: {
    buyerBookingCounts: "/api/booking/buyer-booking-counts",
    inventoryCorrectionsCount: "/api/inventory-corrections/buyer/count"
  },
  supplier: {
    pendingBookingsCount: "/api/booking/count-bookings",
    inventoryCorrectionsCount: "/api/inventory-corrections/supplier/count",
    pendingInventoryActivityCount: "/api/pending-inventory-activities/count",
  },
  admin: {
    //put any admin observable endpoints here
  }
});

export const optionsEndpoints = Object.freeze({
  admin: {
    location: "/api/options/admin/location",
    account: {
      buyer: "/api/options/admin/account/buyer",
      supplier: "/api/options/admin/account/supplier",
    },
    payouts: {
      statuses: "/api/options/admin/payouts/statuses",
    }
  },
  buyer: {
    inventoryLocation: "/api/options/buyer/inventory/location",
    inventoryActivity: {
      location: "/api/options/buyer/inventory-activity/location",
      supplier: "/api/options/buyer/inventory-activity/supplier"
    },
    location: "/api/options/buyer/location",
    locationFavorite: {
      city: "/api/options/buyer/location-favorite/city",
      state: "/api/options/buyer/location-favorite/state",
    }
  },
  supplier: {
    location: "/api/options/supplier/location",
    account: {
      buyer: "/api/options/supplier/account/buyer",
    },
  },
  general: {
    invoicesGroupSummary: "/api/types/invoicesGroupSummaryOptions",
    assets: "/api/types/assets",
    taxStatus: "/api/types/taxStatus",
    assetTypeGroup: "/api/types/assetTypeGroupOptions",
    bookingReasonDeclined: "/api/types/bookingReasonDeclinedOptions",
  }
});

export const notificationEndpoints = Object.freeze({
  buyer: {
    get: "/api/buyer/notification-settings",
    patch: "/api/buyer/notification-settings",
  },
  supplier: {
    get: "/api/supplier/notification-settings",
    patch: "/api/supplier/notification-settings",
  }
});

export const spaceRequestEndpoints = Object.freeze({
  submit: "/api/location/space-requests",
});
/**
 * Assistant backend.
 *
 * `act` is the conversational agent: it takes free text and answers by calling
 * allow-listed, read-only API operations as the signed-in user, then composing
 * a reply. It produces the answer itself rather than choosing a pre-written
 * skill, which is what lets it field questions no local skill covers. Every
 * operation it can reach is scoped to the caller's own token, so it grants no
 * access the user did not already have.
 *
 * Skill *selection* happens entirely in the browser (a deterministic matcher),
 * so there is no `plan` endpoint here — only `act`, the fallback the matcher
 * defers to.
 */
export const assistantEndpoints = Object.freeze({
  act: "/api/assistant/act",
});
